import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Check, FileSpreadsheet, Trash2, TriangleAlert, Upload } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { usePode } from '@/stores/authStore';
import { SITUACAO, capitalizar, competenciaPorExtenso, erroDaApi, reais, type FolhaDaLista } from './tipos';

/** "2026-08-12" ou "2026-08-13T10:00:00-03:00" viram "12/08/2026". */
function dataCurta(data: string): string {
  const [ano, mes, dia] = data.slice(0, 10).split('-');

  return `${dia}/${mes}/${ano}`;
}

/**
 * As competências de folha, da mais nova para a mais velha.
 *
 * É a porta do módulo: de um lado o que já foi para o ERP, do outro o mês que
 * está em cima da mesa — com o que ainda falta o escritório mandar.
 */
export default function FolhasPage() {
  const podeImportar = usePode('folha.importar');
  const queryClient = useQueryClient();
  const [excluindo, setExcluindo] = useState<FolhaDaLista | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['folhas'],
    queryFn: async () => (await api.get<{ data: FolhaDaLista[] }>('/folhas')).data.data,
  });

  const excluir = useMutation({
    mutationFn: async (folha: FolhaDaLista) => (await api.delete(`/folhas/${folha.id}`)).data,
    onSuccess: () => {
      setExcluindo(null);
      void queryClient.invalidateQueries({ queryKey: ['folhas'] });
    },
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível carregar as folhas.</p>;
  }

  const folhas = data ?? [];

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Folha por setor</h1>
          <p className="mt-xs text-[13px] text-muted">
            O PDF que o escritório manda vira a planilha de lançamentos do ERP, conferida.
          </p>
        </div>

        {podeImportar && (
          <Link
            to="/folha/importar"
            className="flex h-10 items-center gap-sm rounded-lg bg-accent px-md text-[14px] font-bold text-white hover:bg-accent/90"
          >
            <Upload size={16} />
            Importar folha do mês
          </Link>
        )}
      </header>

      {folhas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface px-lg py-2xl text-center">
          <FileSpreadsheet size={28} className="mx-auto text-muted" />
          <p className="mt-md text-[15px] font-bold text-ink">Nenhuma competência importada ainda</p>
          <p className="mt-xs text-[13px] text-muted">
            Comece pelo PDF da folha — o resumo por setor que a contabilidade envia todo mês.
          </p>
        </div>
      ) : (
        <ul className="grid gap-md sm:grid-cols-2 xl:grid-cols-3">
          {folhas.map((folha) => {
            const { impostos } = folha;
            const faltamRelatorios = impostos.com_relatorio < impostos.setores;

            return (
              <li key={folha.id} className="relative">
                <Link
                  to={`/folha/${folha.id}`}
                  className="flex h-full flex-col rounded-lg border border-border bg-surface p-md transition-colors hover:border-accent"
                >
                  <div className="flex items-start justify-between gap-sm">
                    <div>
                      <p className="flex items-center gap-xs text-[13px] text-muted">
                        <CalendarDays size={14} />
                        Competência
                      </p>
                      <p className="mt-[2px] text-[18px] font-bold text-ink">
                        {capitalizar(competenciaPorExtenso(folha.competencia))}
                      </p>
                    </div>
                    <span className={`rounded-full px-sm py-[2px] text-[11px] font-bold whitespace-nowrap ${SITUACAO[folha.status].classe}`}>
                      {SITUACAO[folha.status].rotulo}
                    </span>
                  </div>

                  <dl className="mt-md grid grid-cols-2 gap-x-md gap-y-sm text-[13px]">
                    <div>
                      <dt className="text-muted">Líquido a pagar</dt>
                      <dd className="text-[15px] font-bold text-ink tabular-nums">{reais.format(folha.empresa.liquido)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">Funcionários</dt>
                      <dd className="text-[15px] font-bold text-ink tabular-nums">{folha.empresa.funcionarios}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">Proventos</dt>
                      <dd className="text-ink tabular-nums">{reais.format(folha.empresa.proventos)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">Descontos</dt>
                      <dd className="text-ink tabular-nums">{reais.format(folha.empresa.descontos)}</dd>
                    </div>
                  </dl>

                  <ul className="mt-md space-y-[3px] border-t border-border pt-sm text-[13px]">
                    <li className={`flex items-center gap-xs ${faltamRelatorios ? 'text-warning' : 'text-ink'}`}>
                      {faltamRelatorios ? <TriangleAlert size={14} /> : <Check size={14} className="text-success" />}
                      Impostos: {impostos.com_relatorio} de {impostos.setores} setores com relatório
                    </li>
                    <li className={`flex items-center gap-xs ${impostos.totalizador ? 'text-ink' : 'text-warning'}`}>
                      {impostos.totalizador ? <Check size={14} className="text-success" /> : <TriangleAlert size={14} />}
                      {impostos.totalizador ? 'Totalizador recebido' : 'Totalizador ainda não chegou'}
                    </li>
                    <li className="flex items-center gap-xs text-ink">
                      <FileSpreadsheet size={14} className="text-muted" />
                      {folha.linhas} lançamentos para o ERP
                    </li>
                  </ul>

                  <p className="mt-sm text-[12px] text-muted">
                    {[
                      folha.processado_em && `Folha processada em ${dataCurta(folha.processado_em)}`,
                      folha.importado_em && `importada em ${dataCurta(folha.importado_em)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>

                  <p className="mt-auto flex items-center gap-xs pt-md text-[12px] font-bold text-accent">
                    Abrir a competência
                    <ArrowRight size={14} />
                  </p>
                </Link>

                {podeImportar && (
                  <button
                    type="button"
                    onClick={() => {
                      excluir.reset();
                      setExcluindo(folha);
                    }}
                    title="Excluir a competência"
                    className="absolute right-sm bottom-sm rounded-md p-[6px] text-muted hover:bg-danger/10 hover:text-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={excluindo !== null} onOpenChange={(aberto) => !excluir.isPending && !aberto && setExcluindo(null)}>
        <DialogContent className="max-w-[500px] p-lg">
          <DialogTitle>Excluir a competência {excluindo && competenciaPorExtenso(excluindo.competencia)}?</DialogTitle>
          <DialogDescription className="mt-xs">
            Saem a folha importada, os relatórios de imposto, os lançamentos e o PDF guardado. Rubricas, verbas e centros
            de custo ficam. Não dá para desfazer.
          </DialogDescription>
          {excluindo?.status === 'exportada' && (
            <p className="mt-sm flex gap-xs rounded-md bg-warning/10 p-sm text-[13px] text-ink">
              <TriangleAlert size={16} className="mt-[1px] shrink-0 text-warning" />
              Esta competência já foi enviada ao ERP. Excluir aqui não desfaz o lote importado no Compumate.
            </p>
          )}
          {excluir.isError && (
            <p className="mt-sm text-[13px] font-bold text-danger">{erroDaApi(excluir.error, 'Não foi possível excluir a competência.')}</p>
          )}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setExcluindo(null)}
              disabled={excluir.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => excluindo && excluir.mutate(excluindo)}
              disabled={excluir.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90 disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden="true" />
              {excluir.isPending ? 'Excluindo…' : 'Excluir competência'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
